import React from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import {
  ASYNC_EXCHANGE_STATUS,
  type AsyncExchangeClientStatus,
  DONE_EVENT_TYPE,
} from '~/components/AiChat/constants/asyncChat';
import type { OrganizedChatUpdates } from '~/components/AiChat/hooks/useChatRealTimeUpdates';
import {
  type ExchangeRunnerState,
  type ExchangeTransport,
  isExchangeInFlight,
  useExchangeRunner,
} from '~/components/AiChat/hooks/useExchangeRunner';
import type { AiChatExchangeApiResponse, AsyncChatSubmitResponse, IssueTokenResponse } from '~/components/AiChat/types';
import type { SingleChatExchangeResponse } from '~/server_shared/openapi/data-contracts';

export interface AsyncExchangeState {
  exchangeId: string;
  userInput: string;
  contextDocumentIds: number[];
  promptKey?: string;
  status: AsyncExchangeClientStatus;
  error?: string | null;
}

export interface UseAsyncChatExchangeParams {
  baseRoute: string;
  chatInternalId: number | null;
  organizedRealTimeUpdates: OrganizedChatUpdates;
  // Live Firestore subscription connected? Drives the poll cadence (slow backstop vs fast fallback).
  realTimeConnected: boolean;
  exchanges: AiChatExchangeApiResponse[];
  // Called when an exchange resolves as completed — hand the answer back to useChat to render.
  onResolved: (exchange: AiChatExchangeApiResponse) => void;
  // Push a fresh real-time token (from submit/retry) up to useChat so it can re-point the live
  // subscription. The token also has non-async sources (chat load), so useChat owns it, not this hook.
  onTokenRefresh: (token: IssueTokenResponse) => void;
}

export interface UseAsyncChatExchangeReturn {
  activeAsyncExchange: AsyncExchangeState | null;
  isFetching: boolean;
  submitAsync: (userInput: string, contextDocumentIds: number[], promptKey?: string) => Promise<void>;
  submitAsyncRegenerate: (exchangeId: string, userInput: string, contextDocumentIds: number[]) => Promise<void>;
  // Re-submits the failed exchange (mirrors submitAsync — retry must not skip the `done` fast path).
  retryExchange: () => Promise<void>;
}

// What a claim chat exchange sends, kept by the runner so Retry can re-send it.
interface ClaimChatInput {
  userInput: string;
  contextDocumentIds: number[];
  promptKey?: string;
}

// Regenerate an existing exchange instead of sending a new one. Retry does not repeat it, so a failed
// regenerate is retried as a plain submit of the same input.
interface ClaimChatSubmitExtra {
  regenerateExchangeId: string;
}

type ClaimChatTransport = ExchangeTransport<
  ClaimChatInput,
  ClaimChatSubmitExtra,
  AiChatExchangeApiResponse,
  AiChatExchangeApiResponse,
  IssueTokenResponse
>;

// The pass-through GET returns string ids / ISO dates; the exchange list is typed with number id +
// Date. Runtime ids are effectively opaque strings across the chat code, so cast rather than Number().
const toApiExchange = (
  exchange: SingleChatExchangeResponse,
  chatInternalId: number | null
): AiChatExchangeApiResponse => ({
  id: exchange.id as unknown as number,
  chat_id: chatInternalId,
  user_input: exchange.user_input,
  response: exchange.response ?? null,
  response_time: exchange.response_time ?? null,
  user_feedback_score: exchange.user_feedback_score ?? undefined,
  user_feedback_text: exchange.user_feedback_text ?? undefined,
  documents_used: exchange.documents_used ?? [],
  context_document_ids: exchange.context_document_ids ?? [],
  created_at: exchange.created_at ? new Date(exchange.created_at) : new Date(),
});

// A persisted exchange with no answer is still running. Falsy response/response_time = no answer yet.
const isPersistedExchangeInFlight = (exchange: AiChatExchangeApiResponse): boolean =>
  !exchange.response && !exchange.response_time;

// The context documents are recovered too, so a retry of a recovered exchange re-sends them.
const inputOfPersistedExchange = (exchange: AiChatExchangeApiResponse): ClaimChatInput => ({
  userInput: exchange.user_input,
  contextDocumentIds: exchange.context_document_ids,
});

// Flatten the runner's state into the shape the chat UI reads. `rest` is `status`, plus `error` only
// when the runner set one, so a processing exchange carries no `error` key.
const toAsyncExchangeState = (active: ExchangeRunnerState<ClaimChatInput> | null): AsyncExchangeState | null => {
  if (isNil(active)) return null;
  const { exchangeId, input, ...rest } = active;
  return { exchangeId, ...input, ...rest };
};

export const useAsyncChatExchange = ({
  baseRoute,
  chatInternalId,
  organizedRealTimeUpdates,
  realTimeConnected,
  exchanges,
  onResolved,
  onTokenRefresh,
}: UseAsyncChatExchangeParams): UseAsyncChatExchangeReturn => {
  // Read the latest chat id inside the transport's requests without rebuilding the transport per chat.
  const chatInternalIdRef = React.useRef(chatInternalId);
  React.useEffect(() => {
    chatInternalIdRef.current = chatInternalId;
  }, [chatInternalId]);

  const transport = React.useMemo<ClaimChatTransport>(
    () => ({
      submit: async (input, extra) => {
        // A regenerate sends no user input: it re-runs the original exchange under the fresh id the
        // worker returns.
        const { data } = isNil(extra)
          ? await axios.patch<AsyncChatSubmitResponse>(baseRoute, {
              user_input: input.userInput,
              context_document_ids: input.contextDocumentIds,
              prompt_key: input.promptKey,
              chat_id: chatInternalIdRef.current,
            })
          : await axios.post<AsyncChatSubmitResponse>(
              `${baseRoute}/exchanges/${extra.regenerateExchangeId}/regenerate`,
              { chat_id: chatInternalIdRef.current }
            );
        return { exchangeId: data.exchange_id, realTimeToken: data.real_time_token };
      },
      fetch: async (exchangeId) => {
        const { data } = await axios.get<SingleChatExchangeResponse>(`${baseRoute}/exchanges/${exchangeId}`, {
          params: { chat_id: chatInternalIdRef.current },
        });
        if (data.status === ASYNC_EXCHANGE_STATUS.COMPLETED) {
          return {
            status: ASYNC_EXCHANGE_STATUS.COMPLETED,
            exchange: toApiExchange(data, chatInternalIdRef.current),
          };
        }
        if (data.status === ASYNC_EXCHANGE_STATUS.FAILED) {
          return { status: ASYNC_EXCHANGE_STATUS.FAILED, error: data.error };
        }
        // Still running, or a status this client does not know: keep waiting.
        return { status: ASYNC_EXCHANGE_STATUS.PROCESSING };
      },
      isInFlight: isPersistedExchangeInFlight,
      inputOf: inputOfPersistedExchange,
    }),
    [baseRoute]
  );

  const { active, isFetching, submit, retry, refresh } = useExchangeRunner({
    transport,
    rows: exchanges,
    isConnected: realTimeConnected,
    // A new conversation (or claim) tears the previous one's exchange down.
    resetKey: `${baseRoute}|${chatInternalId}`,
    onResolved,
    onRealTimeToken: onTokenRefresh,
  });

  const activeAsyncExchange = React.useMemo(() => toAsyncExchangeState(active), [active]);

  const submitAsync = React.useCallback(
    (userInput: string, contextDocumentIds: number[], promptKey?: string) =>
      submit({ userInput, contextDocumentIds, promptKey }),
    [submit]
  );

  const submitAsyncRegenerate = React.useCallback(
    (exchangeId: string, userInput: string, contextDocumentIds: number[]) =>
      submit({ userInput, contextDocumentIds }, { regenerateExchangeId: exchangeId }),
    [submit]
  );

  // Happy path: a `done` real-time event for our exchange → fetch the answer immediately.
  React.useEffect(() => {
    if (!isExchangeInFlight(active)) return;
    const { exchangeId } = active;
    const events = [...organizedRealTimeUpdates.pending, ...(organizedRealTimeUpdates.byExchangeId[exchangeId] ?? [])];
    const isDone = events.some((e) => e.chat_exchange_id === exchangeId && e.event_type === DONE_EVENT_TYPE);
    if (isDone) {
      refresh(exchangeId);
    }
  }, [active, organizedRealTimeUpdates, refresh]);

  return {
    activeAsyncExchange,
    isFetching,
    submitAsync,
    submitAsyncRegenerate,
    retryExchange: retry,
  };
};
